import { FormEvent, useState } from "react"
import toast from "react-hot-toast"
import { useModalWindow } from "react-modal-global"
import ZodForm, { useZodFormIssues } from "react-zod-form"
import { coerce } from "zod"

import queryClient from "@/api/client"
import { AppInstance } from "@/entities/app/app.types"
import OCBAppDAO from "@/entities/app/ocb/ocb.dao"
import PopupLayout from "@/services/Modal/layouts/PopupLayout/PopupLayout"
import Button from "@/shared/components/intrinsic/Button/Button"
import Field from "@/shared/components/intrinsic/Field/Field"
import Fields from "@/shared/layouts/Fields/Fields"
import useFormStatus from "@/utils/hooks/useFormStatus"

interface OCBNewAppModalProps {
  companyId: string
  location: {
    id: string
  }
}

const ocbAppDao = new OCBAppDAO

const form = new ZodForm({
  name: coerce.string().trim().min(1, "AppID cannot be empty. Please enter a valid AppID to proceed."),
  label: coerce.string().min(1, "Name cannot be empty. Please enter a valid name to proceed.")
}, { noTransform: true })

function OCBNewAppModal(props: OCBNewAppModalProps) {
  const modal = useModalWindow()
  const [pending, setPending] = useState<boolean>(false)
  const formStatus = useFormStatus(form, onSubmit)


  async function onOCBCreate(name: string, label: string) {
    const queryKey = [ocbAppDao.name, "location", props.location.id]

    const ocbApp = await ocbAppDao.create({
      name: name.trim(),
      label: label,
      companyId: props.companyId,
      locationId: props.location.id
    })

    queryClient.setQueryData<AppInstance[]>(queryKey, apps => apps && [...apps, ocbApp])
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    const fields = form.parseAllFields(event)
    if (fields == null) return


    try {
      setPending(true)
      await onOCBCreate(fields.name, fields.label)
      modal.close()
      toast.success("OCB App instance has been created")
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      }
    } finally {
      setPending(false)
    }
  }

  const { fieldIssues } = useZodFormIssues(form)

  return (
    <PopupLayout title="Create OCB APP Instance">
      <form onChange={form.parseCurrentField.bind(form)} onSubmit={formStatus.submit}>
        <Fields>
          <Field
            type="text"
            name={form.fields.name}
            placeholder="App ID"
            customValidity={fieldIssues.name}
          />
          <Field
            type="text"
            name={form.fields.label}
            placeholder="Name"
            customValidity={fieldIssues.label}
          />
          <Button color="white" type="submit" {...formStatus} pending={pending}>Create</Button>
        </Fields>
      </form>
    </PopupLayout>
  )
}

export default OCBNewAppModal
